/**
 * Users Tab Component
 *
 * User management interface for admins
 * Features: List users, create, update, block/unblock, archive, delete
 * Superadmins can assign users to multiple tenants during creation
 * Tenant admins can pick which tenant to manage users for
 * Server-side search and pagination with window scroll
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import {
  listUsers,
  createUser,
  toggleUserAvatar,
  listTenants,
  listRoles,
  getMyAdminTenants,
  getTenantFeatures,
  type Tenant,
  type PaginatedUsersResponse,
} from '@/services/adminApi';
import type { Role } from '@/types/roles';
import type { User, UserCreateRequest, UserRole, UserStatus } from '@/types/user';
import PasswordField from './PasswordField';

const PAGE_SIZE = 50;

export default function UsersTab() {
  const navigate = useNavigate();
  const { t } = useTranslation('admin');
  const { user: currentUser } = useAuth();
  const { tenant: currentTenant } = useTenant();
  const [users, setUsers] = useState<User[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterRole, setFilterRole] = useState<string>('');

  // Search state with debounce
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Tenant state
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenants, setSelectedTenants] = useState<string[]>([]);
  const isSuperadmin = currentUser?.is_superadmin === true;
  const isTenantAdmin = !isSuperadmin && currentUser?.role === 'admin';

  // Tenant admin: admin tenants for picker
  const [adminTenants, setAdminTenants] = useState<Array<{ tenant_id: string; name: string; domain: string }>>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');

  // ACL roles from roles table
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);

  // Sentinel ref for infinite scroll via IntersectionObserver
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Form state for creating user
  const [newUser, setNewUser] = useState<UserCreateRequest>({
    username: '',
    password: '',
    full_name: '',
    email: '',
    role: 'user' as UserRole,
    preferred_language: 'en',
  });
  const [passwordConfirm, setPasswordConfirm] = useState('');

  // App permissions state for create form
  const ALL_APP_MODULES = ['calendar', 'email', 'knowledge_base', 'cogito', 'tasks', 'disk', 'people', 'chat', 'contractors'] as const;
  const [appPermissions, setAppPermissions] = useState<Record<string, boolean>>({
    calendar: true, email: true, knowledge_base: true, cogito: true, tasks: true, disk: true, people: true, chat: true, contractors: true,
  });
  const [tenantFeatures, setTenantFeatures] = useState<Record<string, boolean>>({});

  // Password validation
  const passwordsMatch = useMemo(
    () => newUser.password === passwordConfirm,
    [newUser.password, passwordConfirm]
  );
  const passwordValid = useMemo(
    () => newUser.password.length >= 8,
    [newUser.password]
  );
  const usernameValid = useMemo(
    () => newUser.username === '' || /^[a-z0-9]+$/.test(newUser.username),
    [newUser.username]
  );
  const canCreateUser = useMemo(
    () =>
      newUser.username.trim() !== '' &&
      usernameValid &&
      newUser.email.trim() !== '' &&
      newUser.full_name.trim() !== '' &&
      passwordValid &&
      passwordsMatch,
    [newUser.username, usernameValid, newUser.email, newUser.full_name, passwordValid, passwordsMatch]
  );

  const hasMore = users.length < totalUsers;

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch initial page of users
  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const data: PaginatedUsersResponse = await listUsers(
        filterStatus || undefined,
        filterRole || undefined,
        debouncedSearch || undefined,
        PAGE_SIZE,
        0,
        selectedTenantId || undefined
      );
      setUsers(data.users || []);
      setTotalUsers(data.total || 0);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load users';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [filterStatus, filterRole, debouncedSearch, selectedTenantId]);

  // Load more users for infinite scroll
  const loadMoreUsers = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    try {
      const data: PaginatedUsersResponse = await listUsers(
        filterStatus || undefined,
        filterRole || undefined,
        debouncedSearch || undefined,
        PAGE_SIZE,
        users.length,
        selectedTenantId || undefined
      );
      setUsers(prev => [...prev, ...data.users]);
      setTotalUsers(data.total || 0);
    } catch (err: unknown) {
      console.error('Failed to load more users:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [filterStatus, filterRole, debouncedSearch, selectedTenantId, users.length, isLoadingMore, hasMore]);

  // Fetch roles from ACL table
  const fetchRoles = async () => {
    try {
      const data = await listRoles();
      setAvailableRoles(data.roles);
    } catch (err: unknown) {
      console.error('Failed to fetch roles:', err);
    }
  };

  // Fetch tenants for superadmin (used in create form and tenant picker)
  const fetchTenants = async () => {
    if (!isSuperadmin) return;
    try {
      const data = await listTenants();
      setTenants(data.tenants);
      // Set default selectedTenantId to current tenant if not already set
      if (!selectedTenantId && currentTenant?.id) {
        const match = data.tenants.find((t: Tenant) => t.id === currentTenant.id);
        if (match) {
          setSelectedTenantId(currentTenant.id);
        }
      }
    } catch (err: unknown) {
      console.error('Failed to fetch tenants:', err);
    }
  };

  // Fetch tenant features and set default app permissions accordingly
  const fetchTenantFeaturesForForm = async (tenantId: string) => {
    try {
      const data = await getTenantFeatures(tenantId);
      const features = data.features || {};
      setTenantFeatures(features);
      // Default permissions: enabled only if tenant allows it
      const perms: Record<string, boolean> = {};
      for (const key of ALL_APP_MODULES) {
        perms[key] = features[key] !== false; // opt-out model: missing = enabled
      }
      setAppPermissions(perms);
    } catch (err: unknown) {
      console.error('Failed to fetch tenant features:', err);
      // Fallback: all enabled
      setTenantFeatures({});
      setAppPermissions({ calendar: true, email: true, knowledge_base: true, cogito: true, tasks: true, disk: true, people: true });
    }
  };

  // Fetch admin tenants for tenant admin picker
  useEffect(() => {
    if (isTenantAdmin) {
      getMyAdminTenants().then(data => {
        setAdminTenants(data.tenants);
        if (data.tenants.length > 0 && !selectedTenantId) {
          // Default to current tenant if it's in the admin list, else first
          const currentId = currentTenant?.id;
          const match = currentId && data.tenants.find((t: { tenant_id: string }) => t.tenant_id === currentId);
          setSelectedTenantId(match ? currentId : data.tenants[0].tenant_id);
        }
      }).catch(err => console.error('Failed to fetch admin tenants:', err));
    }
  }, [isTenantAdmin]);

  // Fetch tenants for superadmin on mount (for tenant picker)
  useEffect(() => {
    if (isSuperadmin) {
      fetchTenants();
    }
  }, [isSuperadmin]);

  // Reset and refetch when filters change
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // IntersectionObserver for auto-loading more
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore && !isLoading) {
          loadMoreUsers();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, isLoading, loadMoreUsers]);

  // Fetch tenants and roles when form opens
  useEffect(() => {
    if (showCreateForm) {
      fetchRoles();
      if (isSuperadmin) {
        fetchTenants();
      }
    }
  }, [showCreateForm, isSuperadmin]);

  // Load tenant features for the primary selected tenant
  useEffect(() => {
    if (!showCreateForm) return;
    const tenantIdForFeatures = isTenantAdmin
      ? selectedTenantId
      : (isSuperadmin && selectedTenants.length > 0 ? selectedTenants[0] : undefined);
    if (tenantIdForFeatures) {
      fetchTenantFeaturesForForm(tenantIdForFeatures);
    } else {
      // No tenant selected - reset to all enabled
      setTenantFeatures({});
      setAppPermissions({ calendar: true, email: true, knowledge_base: true, cogito: true, tasks: true, disk: true, people: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreateForm, isTenantAdmin, selectedTenantId, isSuperadmin, selectedTenants[0]]);

  // Tenant toggle handler (for create form)
  const handleTenantToggle = (tenantId: string) => {
    setSelectedTenants(prev =>
      prev.includes(tenantId)
        ? prev.filter(id => id !== tenantId)
        : [...prev, tenantId]
    );
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canCreateUser) return;
    try {
      // Determine tenant_ids for tenant assignment
      let tenantIds: string[] | undefined;
      if (isSuperadmin && selectedTenants.length > 0) {
        tenantIds = selectedTenants;
      } else if (isTenantAdmin) {
        tenantIds = selectedTenants.length > 0 ? selectedTenants : (selectedTenantId ? [selectedTenantId] : undefined);
      }

      const userData: UserCreateRequest = {
        ...newUser,
        app_permissions: appPermissions,
        ...(tenantIds && { tenant_ids: tenantIds }),
      };
      await createUser(userData);
      setShowCreateForm(false);
      setNewUser({ username: '', password: '', full_name: '', email: '', role: 'user' as UserRole, preferred_language: 'en' });
      setPasswordConfirm('');
      setSelectedTenants([]);
      setAppPermissions({ calendar: true, email: true, knowledge_base: true, cogito: true, tasks: true, disk: true, people: true });
      setTenantFeatures({});
      fetchUsers();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create user';
      alert(errorMessage);
    }
  };

  const handleToggleAvatar = async (userId: number, currentlyEnabled: boolean) => {
    try {
      await toggleUserAvatar(userId, !currentlyEnabled);
      fetchUsers();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to toggle avatar';
      alert(errorMessage);
    }
  };

  const getStatusBadge = (status: UserStatus) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      blocked: 'bg-red-100 text-red-800',
      archived: 'bg-gray-100 text-gray-800',
    };
    return `px-2 py-1 text-xs font-medium rounded-full ${colors[status] || 'bg-gray-100 text-gray-800'}`;
  };

  const getRoleBadge = (role: UserRole) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-800',
      user: 'bg-blue-100 text-blue-800',
    };
    return `px-2 py-1 text-xs font-medium rounded-full ${colors[role] || 'bg-gray-100 text-gray-800'}`;
  };

  /**
   * Check if a user is protected from dangerous operations
   * Protected users: root superadmin (always), admin (unless viewer is root), current user
   */
  const isProtectedUser = (user: User): boolean => {
    // Root account is always protected
    if (user.username === 'root') {
      return true;
    }
    // Admin account is protected unless the current user is root superadmin
    if (user.username === 'admin' && currentUser?.username !== 'root') {
      return true;
    }
    // Protect current logged-in user (prevent self-harm)
    if (currentUser && user.username === currentUser.username) {
      return true;
    }
    return false;
  };

  return (
    <div>
      {/* Header with Actions */}
      <div className="mb-6 flex flex-wrap justify-between items-center gap-4">
        <div className="flex gap-4 flex-wrap">
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="px-4 py-2 bg-black text-white rounded-md text-sm font-medium hover:bg-gray-800 transition-colors"
          >
            {showCreateForm ? `✕ ${t('users.cancel')}` : `+ ${t('users.createUser')}`}
          </button>

          {/* Search */}
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('users.searchPlaceholder')}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          {/* Filters */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">{t('users.allStatuses')}</option>
            <option value="active">{t('users.active')}</option>
            <option value="blocked">{t('users.blocked')}</option>
            <option value="archived">{t('users.archived')}</option>
          </select>

          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">{t('users.allRoles')}</option>
            <option value="admin">{t('users.admin')}</option>
            <option value="user">{t('users.user')}</option>
          </select>

          {/* Tenant Picker (superadmin or tenant admin) */}
          {isSuperadmin && tenants.length > 0 && (
            <select
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="">{t('users.allTenants', 'All tenants')}</option>
              {tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
              ))}
            </select>
          )}
          {isTenantAdmin && adminTenants.length > 0 && (
            <select
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="">{t('users.allTenants', 'All tenants')}</option>
              {adminTenants.map((at) => (
                <option key={at.tenant_id} value={at.tenant_id}>{at.name}</option>
              ))}
            </select>
          )}
        </div>

        {/* Total count */}
        {!isLoading && totalUsers > 0 && (
          <span className="text-sm text-gray-500">{totalUsers} {t('gdpr.total').toLowerCase()}</span>
        )}
      </div>

      {/* Create User Form */}
      {showCreateForm && (
        <div className="mb-6 bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">{t('createUserForm.title')}</h3>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('createUserForm.username')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder={t('createUserForm.username')}
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '') })}
                  required
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    newUser.username && !usernameValid ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {newUser.username && !usernameValid && (
                  <p className="text-xs text-red-500 mt-1">{t('createUserForm.usernameFormat')}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('createUserForm.email')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  placeholder={t('createUserForm.email')}
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('createUserForm.fullName')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder={t('createUserForm.fullName')}
                  value={newUser.full_name}
                  onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('createUserForm.role')}
                </label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value as UserRole })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {availableRoles.length > 0 ? (
                    availableRoles
                      .filter(r => isSuperadmin || r.name !== 'superadmin')
                      .map(r => (
                        <option key={r.id} value={r.name}>{r.display_name}</option>
                      ))
                  ) : (
                    <>
                      <option value="user">{t('users.user')}</option>
                      <option value="admin">{t('users.admin')}</option>
                    </>
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('createUserForm.preferredLanguage')}
                </label>
                <select
                  value={newUser.preferred_language || 'en'}
                  onChange={(e) => setNewUser({ ...newUser, preferred_language: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="en">English</option>
                  <option value="it">Italiano</option>
                </select>
              </div>
            </div>

            {/* Tenant Selection (Superadmin or multi-tenant admin) */}
            {isSuperadmin && tenants.length > 0 && (
              <div className="pt-2 border-t border-gray-100">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('createUserForm.assignToTenants')}
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  {t('createUserForm.tenantsDescription')}
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {tenants.map((tenant) => (
                    <label
                      key={tenant.id}
                      className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedTenants.includes(tenant.id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedTenants.includes(tenant.id)}
                        onChange={() => handleTenantToggle(tenant.id)}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {tenant.name}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {tenant.domain}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {isTenantAdmin && adminTenants.length > 1 && (
              <div className="pt-2 border-t border-gray-100">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('createUserForm.assignToTenants')}
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  {t('createUserForm.tenantsDescription')}
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {adminTenants.map((at) => (
                    <label
                      key={at.tenant_id}
                      className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedTenants.includes(at.tenant_id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedTenants.includes(at.tenant_id)}
                        onChange={() => handleTenantToggle(at.tenant_id)}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {at.name}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {at.domain}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* App Permissions */}
            <div className="pt-2 border-t border-gray-100">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('createUserForm.appPermissions')}
              </label>
              <p className="text-xs text-gray-500 mb-3">
                {t('createUserForm.appPermissionsDescription')}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {ALL_APP_MODULES.map((key) => {
                  const tenantDisabled = tenantFeatures[key] === false;
                  const enabled = appPermissions[key] ?? true;
                  return (
                    <div
                      key={key}
                      className={`flex items-center justify-between bg-white rounded-lg px-4 py-3 border ${
                        tenantDisabled ? 'border-gray-100 bg-gray-50' : 'border-gray-200'
                      }`}
                    >
                      <span className={`text-sm ${tenantDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
                        {t(`userDetail.${key === 'knowledge_base' ? 'kb' : key}`)}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          if (!tenantDisabled) {
                            setAppPermissions(prev => ({ ...prev, [key]: !prev[key] }));
                          }
                        }}
                        disabled={tenantDisabled}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          tenantDisabled
                            ? 'bg-gray-200 cursor-not-allowed'
                            : enabled
                              ? 'bg-blue-600'
                              : 'bg-gray-300'
                        }`}
                        title={tenantDisabled ? t('createUserForm.disabledByTenant') : undefined}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            enabled && !tenantDisabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Password fields */}
            <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-100">
              <PasswordField
                label={t('createUserForm.password')}
                value={newUser.password}
                onChange={(value) => setNewUser({ ...newUser, password: value })}
                placeholder={t('createUserForm.password')}
                required
                showGenerator
                showStrength
                error={
                  newUser.password && !passwordValid
                    ? t('createUserForm.passwordMustBe8Chars')
                    : undefined
                }
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('createUserForm.confirmPassword')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  placeholder={t('createUserForm.confirmPassword')}
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  required
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    passwordConfirm && !passwordsMatch
                      ? 'border-red-500'
                      : 'border-gray-300'
                  }`}
                />
                {passwordConfirm && (
                  <p
                    className={`text-xs mt-1 flex items-center gap-1 ${
                      passwordsMatch ? 'text-green-600' : 'text-red-500'
                    }`}
                  >
                    {passwordsMatch ? (
                      <>
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {t('createUserForm.passwordsMatch')}
                      </>
                    ) : (
                      <>
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {t('createUserForm.passwordsDoNotMatch')}
                      </>
                    )}
                  </p>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={!canCreateUser}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                canCreateUser
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {t('createUserForm.createUser')}
            </button>
          </form>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md text-red-800">
          {error}
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-12">
          <p className="text-gray-500">{t('users.loadingUsers')}</p>
        </div>
      )}

      {/* Users List */}
      {!isLoading && users.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* Header */}
          <div className="flex items-center px-6 py-3 bg-gray-50 border-b border-gray-200">
            <div className="flex-[2] text-left text-xs font-medium text-gray-500 uppercase">{t('users.user')}</div>
            <div className="flex-[2] text-left text-xs font-medium text-gray-500 uppercase">{t('createUserForm.email')}</div>
            <div className="flex-1 text-left text-xs font-medium text-gray-500 uppercase">{t('createUserForm.role')}</div>
            <div className="flex-1 text-left text-xs font-medium text-gray-500 uppercase">{t('userDetail.status')}</div>
            <div className="w-16 text-center text-xs font-medium text-gray-500 uppercase">{t('users.avatar')}</div>
            <div className="flex-1 text-left text-xs font-medium text-gray-500 uppercase">{t('users.lastLogin')}</div>
            <div className="w-40 text-right text-xs font-medium text-gray-500 uppercase">{t('gdpr.actions')}</div>
          </div>

          {/* Rows */}
          {users.map((user) => (
            <div key={user.id} className="flex items-center px-6 py-3 hover:bg-gray-50 border-b border-gray-100">
              {/* User */}
              <div className="flex-[2] min-w-0 pr-4">
                <div className="text-sm font-medium text-gray-900 truncate">{user.full_name}</div>
                <div className="text-sm text-gray-500 truncate">@{user.username}</div>
              </div>
              {/* Email */}
              <div className="flex-[2] min-w-0 pr-4">
                <div className="text-sm text-gray-500 truncate">{user.email}</div>
              </div>
              {/* Role */}
              <div className="flex-1 pr-4">
                <span className={getRoleBadge(user.role)}>{user.role}</span>
              </div>
              {/* Status */}
              <div className="flex-1 pr-4">
                <span className={getStatusBadge(user.status)}>{user.status}</span>
              </div>
              {/* Avatar */}
              <div className="w-16 text-center pr-4">
                <button
                  onClick={() => handleToggleAvatar(user.id, user.avatar_enabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    user.avatar_enabled ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                  title={user.avatar_enabled ? t('users.disableAvatar') : t('users.enableAvatar')}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      user.avatar_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              {/* Last Login */}
              <div className="flex-1 pr-4">
                <span className="text-sm text-gray-500">
                  {user.last_login ? new Date(user.last_login).toLocaleDateString() : t('users.never')}
                </span>
              </div>
              {/* Actions */}
              <div className="w-40 text-right">
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => navigate(`/admin/users/${user.id}/stats`)}
                    className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    {t('users.view')}
                  </button>
                  {isProtectedUser(user) ? (
                    <span className="px-3 py-1.5 text-xs text-gray-400 italic flex items-center">
                      {user.username === 'root' ? t('users.rootAdmin') : user.username === 'admin' ? t('users.protected') : t('users.currentUser')}
                    </span>
                  ) : (
                    <button
                      onClick={() => navigate(`/admin/users/${user.id}`)}
                      className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    >
                      {t('users.manage')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Sentinel for auto-loading more */}
          {hasMore && (
            <div ref={sentinelRef} className="flex items-center justify-center py-4">
              {isLoadingMore ? (
                <p className="text-sm text-gray-500">{t('users.loadingUsers')}</p>
              ) : (
                <p className="text-sm text-gray-400">Scroll for more...</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && users.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500">{t('users.noUsersFound')}</p>
        </div>
      )}
    </div>
  );
}
